import axios from "axios";
import {
    clearLoginSession,
    getRefreshToken,
    getStoredUsername,
    setAccessToken,
    setLoginSession,
} from "../utils/auth";
import { showToast } from "../utils/show_toast";

const API_BASE_URL =
    process.env.REACT_APP_NODE_SERVER_URL || process.env.NODE_SERVER_URL || "";

const REFRESH_URL = "/api/user/token/refresh";
const isRefreshUrl = (url) =>
    url.endsWith(REFRESH_URL) || url.endsWith(`${REFRESH_URL}/`);
const BACKOFF_THRESHOLD = 3;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 10000;

const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("access");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const refreshClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // refresh/bootstrap 시 httpOnly refresh 쿠키 전송 (iOS PWA 세션 복구)
});

/**
 * bootstrapSession은 콜드 스타트(localStorage 비워짐) 시 httpOnly refresh 쿠키만으로
 * 세션을 복구한다. 성공하면 새 access+refresh+username 을 localStorage 에 채우고 true,
 * 쿠키가 없거나 만료면 false 를 반환한다 (→ 일반 미인증 흐름 → /login).
 */
export const bootstrapSession = async () => {
    try {
        const res = await refreshClient.post(
            "/api/user/token/bootstrap",
            {},
            { withCredentials: true }
        );
        const { access, refresh, username } = res.data || {};
        if (access) {
            setLoginSession(username || getStoredUsername() || "", access, refresh);
            return true;
        }
    } catch (e) {
        // 유효한 쿠키 세션 없음 — 조용히 실패
    }
    return false;
};

let refreshPromise = null;
let refreshFailureCount = 0;
let backoffUntil = 0;

const requestRefreshToken = async () => {
    const refresh = getRefreshToken();
    if (!refresh) {
        throw new Error("No refresh token available");
    }
    const response = await refreshClient.post("/api/user/token/refresh/", {
        refresh,
    });
    const newAccessToken = response.data?.access;
    if (!newAccessToken) {
        throw new Error("No access token in refresh response");
    }
    setAccessToken(newAccessToken);
    return newAccessToken;
};

const getRefreshPromise = () => {
    if (!refreshPromise) {
        refreshPromise = requestRefreshToken()
            .then((token) => {
                refreshFailureCount = 0;
                backoffUntil = 0;
                return token;
            })
            .catch((err) => {
                refreshFailureCount += 1;
                if (refreshFailureCount >= BACKOFF_THRESHOLD) {
                    const exp = refreshFailureCount - BACKOFF_THRESHOLD;
                    const delay = Math.min(
                        BACKOFF_BASE_MS * Math.pow(2, exp),
                        BACKOFF_CAP_MS
                    );
                    backoffUntil = Date.now() + delay;
                }
                throw err;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

const forceLogout = () => {
    clearLoginSession();
    // window.location.replace로 통일 — SessionWatcher/ErrorBoundary/WS/auth.js와 일관.
    // 뒤로가기로 보호 페이지 재진입 차단 (DAS-63 M1 fast-follow).
    window.location.replace("/login");
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response, config } = error || {};
        if (!response || !config) {
            return Promise.reject(error);
        }

        const status = response.status;
        const url = config.url || "";
        const isRefreshCall = isRefreshUrl(url);

        // refresh 엔드포인트 자체의 401/403 → 즉시 logout (무한 루프 차단)
        if (isRefreshCall && (status === 401 || status === 403)) {
            forceLogout();
            return Promise.reject(error);
        }

        // 403: refresh 시도 없이 toast만 노출
        if (status === 403) {
            showToast({ type: "error", message: "접근 권한이 없습니다" });
            return Promise.reject(error);
        }

        if (status !== 401) {
            return Promise.reject(error);
        }

        if (config._retry) {
            return Promise.reject(error);
        }

        // backoff 중에는 refresh 시도하지 않고 즉시 reject
        if (Date.now() < backoffUntil) {
            return Promise.reject(error);
        }

        config._retry = true;

        try {
            const newAccessToken = await getRefreshPromise();
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient(config);
        } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
        }
    }
);

export { API_BASE_URL };
export default apiClient;
